import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorkflow, useUpdateWorkflow } from '@/hooks/useDocFlow';
import DocFlowCanvas from './editor/DocFlowCanvas';
import type { IWorkflowNode, IWorkflowEdge } from '@/types/docflow';

const WorkflowEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('docflow');
  const { data: workflow, isLoading, error } = useWorkflow(id || '');
  const updateWorkflow = useUpdateWorkflow();

  const [localNodes, setLocalNodes] = useState<IWorkflowNode[]>([]);
  const [localEdges, setLocalEdges] = useState<IWorkflowEdge[]>([]);
  const [localName, setLocalName] = useState('');
  const [localDescription, setLocalDescription] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  // Sync from server on load
  useEffect(() => {
    if (workflow) {
      setLocalNodes(workflow.nodes || []);
      setLocalEdges(workflow.edges || []);
      setLocalName(workflow.name);
      setLocalDescription(workflow.description);
      setIsDirty(false);
    }
  }, [workflow]);

  const handleNodesChange = useCallback((nodes: IWorkflowNode[]) => {
    setLocalNodes(nodes);
    setIsDirty(true);
  }, []);

  const handleEdgesChange = useCallback((edges: IWorkflowEdge[]) => {
    setLocalEdges(edges);
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!id) return;
    updateWorkflow.mutate(
      {
        id,
        data: {
          name: localName,
          description: localDescription,
          nodes: localNodes,
          edges: localEdges,
        },
      },
      {
        onSuccess: () => {
          setIsDirty(false);
        },
      }
    );
  }, [id, localName, localDescription, localNodes, localEdges, updateWorkflow]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{t('error.notFound')}</div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/docflow')}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <input
              type="text"
              value={localName}
              onChange={(e) => { setLocalName(e.target.value); setIsDirty(true); }}
              className="text-lg font-semibold bg-transparent border-none outline-none dark:text-gray-100 w-full"
              placeholder="Workflow Name"
            />
            <input
              type="text"
              value={localDescription}
              onChange={(e) => { setLocalDescription(e.target.value); setIsDirty(true); }}
              className="text-sm text-gray-500 dark:text-gray-400 bg-transparent border-none outline-none w-full"
              placeholder="Description..."
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/app/docflow/${id}/run`)}
            className="px-3 py-2 text-sm bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-800"
          >
            {t('workflow.run')}
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty || updateWorkflow.isLoading}
            className={`px-4 py-2 text-sm rounded-lg ${
              isDirty
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            {updateWorkflow.isLoading ? t('workflow.saving') : t('workflow.save')}
          </button>
        </div>
      </div>

      {/* Canvas */}
      <DocFlowCanvas
        workflowNodes={localNodes}
        workflowEdges={localEdges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
      />
    </div>
  );
};

export default WorkflowEditorPage;
